import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMyMeasuring, refreshMine, loadActive, flushOutbox } from './store';
import { keepPushCurrent } from './phone';
import Recorder from './Recorder';

// The Record button (the director, 7 Oct 2026): there only when the person has
// been given something to measure; never in the way -- drag it anywhere, it
// snaps to the nearest side and this phone remembers where; it shrinks while
// the page scrolls and steps aside while you type; pages get room at the
// bottom so nothing sits under it. On phones it stands above the bottom menu,
// never on it. While a recording runs it is the timer.

const POS_KEY = 'mouldos.recordButton';
const SIZE = 60;
// The bottom menu (Nav.js): 64 px high on screens narrower than 900 px.
const menuHeight = () => (window.innerWidth < 900 ? 64 : 0);
const loadPos = () => { try { return JSON.parse(localStorage.getItem(POS_KEY)) || { side: 'right', bottom: 16 }; } catch { return { side: 'right', bottom: 16 }; } };

export default function RecordDock() {
  const { can, viewAs } = useAuth();
  const mine = useMyMeasuring();
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [pos, setPos] = useState(loadPos);
  const [drag, setDrag] = useState(null);         // { x, y } while dragging
  const [small, setSmall] = useState(false);
  const [typing, setTyping] = useState(false);
  const [menu, setMenu] = useState(menuHeight);
  const [, tick] = useState(0);
  const start = useRef(null);
  // Nothing can be recorded while viewing as someone else (components/ViewAs).
  const allowed = can('record_measurements') && !viewAs;

  // What I have been given: now, every two minutes, and when the phone wakes.
  useEffect(() => {
    if (!allowed) return undefined;
    refreshMine();
    flushOutbox();
    keepPushCurrent();
    const iv = setInterval(refreshMine, 120000);
    // What was finished with no signal goes as soon as there is some.
    const send = setInterval(flushOutbox, 30000);
    const wake = () => { if (document.visibilityState === 'visible') { refreshMine(); flushOutbox(); } };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', flushOutbox);
    return () => { clearInterval(iv); clearInterval(send); document.removeEventListener('visibilitychange', wake); window.removeEventListener('online', flushOutbox); };
  }, [allowed]);
  // A tap on a notification lands here: /today?measure=1.
  useEffect(() => {
    if (!allowed) return;
    const u = new URL(window.location.href);
    if (!u.searchParams.has('measure')) return;
    u.searchParams.delete('measure');
    window.history.replaceState(null, '', u.pathname + u.search);
    setTaskId(null); setOpen(true);
  }, [allowed]);
  // Opened from the Today card, at a measurement.
  useEffect(() => {
    const go = (e) => { setTaskId((e.detail && e.detail.taskId) || null); setOpen(true); };
    window.addEventListener('mouldos:recorder', go);
    return () => window.removeEventListener('mouldos:recorder', go);
  }, []);
  // Out of the way while scrolling and while typing.
  useEffect(() => {
    let t = null;
    const onScroll = () => { setSmall(true); clearTimeout(t); t = setTimeout(() => setSmall(false), 700); };
    const isField = (el) => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
    const onFocus = (e) => setTyping(isField(e.target));
    const onBlur = () => setTimeout(() => setTyping(isField(document.activeElement)), 0);
    const onResize = () => setMenu(menuHeight());
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onBlur);
    return () => { clearTimeout(t); window.removeEventListener('resize', onResize); window.removeEventListener('scroll', onScroll); document.removeEventListener('focusin', onFocus); document.removeEventListener('focusout', onBlur); };
  }, []);

  const active = loadActive();
  const todo = (mine.tasks || []).filter(t => t.status === 'assigned').length;
  const visible = allowed && !open && (todo > 0 || !!active || (mine.running || []).length > 0 || (mine.outbox || []).length > 0);

  // Room at the bottom of every page while the button is there.
  useEffect(() => {
    if (!visible) return undefined;
    const before = document.body.style.paddingBottom;
    document.body.style.paddingBottom = `${menu + SIZE + 36}px`;
    return () => { document.body.style.paddingBottom = before; };
  }, [visible, menu]);
  // The timer ticks while a recording runs.
  useEffect(() => {
    if (!visible || !active) return undefined;
    const iv = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(iv);
  }, [visible, active]);

  if (!visible) return <Recorder open={open} taskId={taskId} onClose={() => { setOpen(false); setTaskId(null); refreshMine(); }} />;

  function down(e) {
    start.current = { x: e.clientX, y: e.clientY, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function move(e) {
    const s = start.current; if (!s) return;
    if (!s.moved && Math.hypot(e.clientX - s.x, e.clientY - s.y) < 8) return;
    s.moved = true;
    setDrag({ x: e.clientX, y: e.clientY });
  }
  function up(e) {
    const s = start.current; start.current = null;
    if (!s || !s.moved) { setDrag(null); setTaskId(null); setOpen(true); return; }
    // Snap to the nearest side; keep clear of the top bar and the bottom menu.
    // Height is kept above the menu, so it stays right if the screen turns.
    const side = e.clientX < window.innerWidth / 2 ? 'left' : 'right';
    const bottom = Math.max(12, Math.min(window.innerHeight - menu - 140, window.innerHeight - menu - e.clientY - SIZE / 2));
    const p = { side, bottom: Math.round(bottom) };
    setPos(p); setDrag(null);
    try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* remembered for this visit only */ }
  }

  const running = active ? Math.floor((Date.now() - Date.parse(active.startedAt)) / 1000) : null;
  const label = running != null
    ? `${String(Math.floor(running / 60)).padStart(2, '0')}:${String(running % 60).padStart(2, '0')}`
    : 'REC';
  const place = drag
    ? { left: drag.x - SIZE / 2, top: drag.y - SIZE / 2 }
    : { [pos.side]: 16, bottom: `calc(${menu + Math.min(pos.bottom, Math.max(12, window.innerHeight - menu - 140))}px + env(safe-area-inset-bottom, 0px))` };
  const scale = small && !drag ? 0.72 : 1;

  return (
    <>
      <button
        aria-label={running != null ? 'Measuring: a recording is running' : `Measuring: ${todo} to do`}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { start.current = null; setDrag(null); }}
        style={{
          position: 'fixed', zIndex: 900, ...place, height: SIZE, minWidth: SIZE, padding: running != null ? '0 16px' : 0,
          borderRadius: SIZE / 2, border: running != null ? '2px solid #5eead4' : 'none', background: '#14b8a6', color: '#0b1215',
          fontWeight: 800, fontSize: running != null ? 15 : 12, letterSpacing: '0.04em', fontFamily: running != null ? 'ui-monospace, monospace' : 'system-ui, sans-serif',
          boxShadow: '0 6px 20px rgba(0,0,0,0.45)', cursor: drag ? 'grabbing' : 'pointer', touchAction: 'none',
          transform: `scale(${scale})`, transition: drag ? 'none' : 'transform 0.2s, opacity 0.2s',
          opacity: typing ? 0 : 1, pointerEvents: typing ? 'none' : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
        {running != null && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0b1215' }} />}
        {label}
        {running == null && todo > 0 && (
          <span style={{ position: 'absolute', top: -4, right: -4, minWidth: 20, height: 20, borderRadius: 10, background: '#f59e0b', color: '#0b1215', fontSize: 11, display: 'grid', placeItems: 'center', padding: '0 5px', fontFamily: 'system-ui, sans-serif' }}>{todo}</span>
        )}
      </button>
      <Recorder open={open} taskId={taskId} onClose={() => { setOpen(false); setTaskId(null); refreshMine(); }} />
    </>
  );
}
